import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { type LucideIcon } from 'lucide-react';
import { kebabCase } from 'lodash';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type SideNavigationLinksProps = ComponentPropsWithoutRef<typeof Button> & {
  collapsed?: boolean;
  label: string;
  icon?: LucideIcon;
  selected: boolean;
  progress?: number;
  iconProps?: { className?: string; strokeWidth?: number };
  /** Set when clicking is handled by a wrapping element instead of an onClick prop, so
   * hover/press affordance still shows. */
  interactive?: boolean;
};

export const SideNavigationLinks = forwardRef<HTMLButtonElement, SideNavigationLinksProps>(
  (
    {
      collapsed,
      label,
      icon: Icon,
      selected,
      onClick,
      className,
      iconProps,
      progress,
      children,
      interactive,
      ...props
    },
    ref,
  ) => {
    const isInteractive = interactive ?? Boolean(onClick);

    return (
      <Button
        ref={ref}
        {...props}
        data-cy={`sidebar-button-${kebabCase(label)}`}
        tooltip={collapsed ? label : undefined}
        className={cn(
          'flex h-12 items-center justify-start text-icon-gray dark:text-icon-gray gap-3 bg-ui-base dark:bg-ui-base px-3 duration-150',
          isInteractive &&
            'cursor-pointer hover:bg-icon-overlay dark:focus-visible:bg-ui-light focus-visible:bg-icon-overlay dark:hover:bg-ui-light',
          selected && 'bg-highlight-base! text-text-inverted!',
          selected && isInteractive && 'focus-visible:bg-highlight-hover-color! hover:bg-highlight-hover-color!',
          collapsed && 'justify-center p-0',
          className,
        )}
        onClick={onClick}
      >
        {children ?? (
          <>
            <div className="flex flex-col items-center gap-0.5">
              {Icon && (
                <Icon
                  className={cn('icon-6', selected ? 'stroke-text-inverted' : 'stroke-icon-base', iconProps?.className)}
                  strokeWidth={iconProps?.strokeWidth ?? 2}
                />
              )}
              {(progress ?? 0) > 0 && (
                <Progress value={progress} className="mt-1 h-0.5 w-6" indicatorClassName="bg-highlight-base" />
              )}
            </div>
            {!collapsed && label}
          </>
        )}
      </Button>
    );
  },
);

SideNavigationLinks.displayName = 'SideNavigationLinks';
